import { cn } from "@/lib/utils";
import { animate } from "animejs";
import { type ReactNode, useEffect, useRef } from "react";

interface AnimatedSectionProps {
  children: ReactNode;
  id?: string;
  className?: string;
  animationType?: "fadeUp" | "fadeDown" | "scaleUp" | "fadeIn";
  delay?: number;
  duration?: number;
}

export function AnimatedSection({
  children,
  id,
  className,
  animationType = "fadeUp",
  delay = 0,
  duration = 1000,
}: AnimatedSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Set initial state
    section.style.opacity = "0";
    switch (animationType) {
      case "fadeUp":
        section.style.transform = "translateY(80px)";
        break;
      case "fadeDown":
        section.style.transform = "translateY(-80px)";
        break;
      case "scaleUp":
        section.style.transform = "scale(0.9)";
        break;
      case "fadeIn":
        // No transform needed
        break;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;

            switch (animationType) {
              case "fadeUp":
                animate(section, {
                  opacity: [0, 1],
                  translateY: [80, 0],
                  duration,
                  delay,
                  ease: "outExpo",
                });
                break;
              case "fadeDown":
                animate(section, {
                  opacity: [0, 1],
                  translateY: [-80, 0],
                  duration,
                  delay,
                  ease: "outExpo",
                });
                break;
              case "scaleUp":
                animate(section, {
                  opacity: [0, 1],
                  scale: [0.9, 1],
                  duration,
                  delay,
                  ease: "outBack",
                });
                break;
              case "fadeIn":
              default:
                animate(section, {
                  opacity: [0, 1],
                  duration,
                  delay,
                  ease: "outExpo",
                });
                break;
            }
          }
        });
      },
      { threshold: 0.15 },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [animationType, delay, duration]);

  return (
    <section id={id} ref={sectionRef} className={cn(className)}>
      {children}
    </section>
  );
}
